import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchBanners } from '../redux/menu/BannersSlice';
import {
  FaInstagram,
  FaPlay,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Back from "../components/Common/Back";

const ReelsScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.profile);
  const { banners, status } = useSelector((state) => state.banner);
  const [selectedStory, setSelectedStory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [useFallbackMode, setUseFallbackMode] = useState(false);

  // Extract reels from the API response
  const reels = banners?.reels || [];

  // Fetch banners when component mounts
  useEffect(() => {
    if (profile?.id) {
      dispatch(fetchBanners(profile.id));
    }
  }, [dispatch, profile?.id]);

  // Function to convert Instagram URL to embed URL
  const convertToEmbedUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return '';
      
      // Clean the URL
      let cleanUrl = url.split('?')[0];
      
      // Extract post/reel ID from URL
      const reelMatch = cleanUrl.match(/\/reel\/([^\/]+)/);
      const postMatch = cleanUrl.match(/\/p\/([^\/]+)/);
      
      if (reelMatch) {
        return `https://www.instagram.com/p/${reelMatch[1]}/embed/`;
      } else if (postMatch) {
        return `https://www.instagram.com/p/${postMatch[1]}/embed/`;
      }
      
      return cleanUrl;
    } catch (error) {
      console.warn('Error converting Instagram URL:', error);
      return '';
    }
  };

  // Transform reels data into stories format
  const stories = reels.map((reel, index) => {
    try {
      // Get the original Instagram URL from the 'link' field
      let originalUrl = reel.link || reel.instagramUrl || reel.url || `https://www.instagram.com/reel/sample${index + 1}/`;
      
      // Convert to embed URL
      const embedUrl = convertToEmbedUrl(originalUrl);
      
      return {
        id: reel.id || `reel-${index}`,
        title: reel.description || reel.name || `Reel ${index + 1}`,
        thumbnail: reel.DocImage?.imageUrl || `https://picsum.photos/150/200?random=${index + 1}`,
        instagramUrl: originalUrl, // Keep original for blockquote
        embedUrl: embedUrl, // Use converted embed URL
        type: "instagram",
      };
    } catch (error) {
      console.warn('Error processing reel:', error, reel);
      return {
        id: `error-reel-${index}`,
        title: reel?.description || `Reel ${index + 1}`,
        thumbnail: `https://picsum.photos/150/200?random=${index + 1}`,
        instagramUrl: `https://www.instagram.com/reel/sample${index + 1}/`,
        embedUrl: `https://www.instagram.com/p/sample${index + 1}/embed/`,
        type: "instagram",
      };
    }
  });

  const finalStories = stories;

  const handleStoryClick = (story) => {
    setSelectedStory(story);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedStory(null);
  };

  const openExternalLink = (url) => {
    window.open(url, "_blank");
  };


  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isModalOpen) {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);


  // Simplified approach - use only iframe to avoid Instagram script errors
  useEffect(() => {
    if (isModalOpen && selectedStory) {
      // Set fallback mode immediately to avoid Instagram script issues
      setUseFallbackMode(true);
      
      // Add global error handler to catch any remaining errors
      const errorHandler = (event) => {
        if (event.filename && event.filename.includes('embed.js')) {
          console.warn('Instagram script error prevented:', event.message);
          event.preventDefault();
          event.stopPropagation();
          return false;
        }
      };
      
      window.addEventListener('error', errorHandler);
      
      return () => {
        window.removeEventListener('error', errorHandler);
      };
    }
  }, [isModalOpen, selectedStory]);

  // Function to convert Instagram URL to embed URL
  const getInstagramEmbedUrl = (instagramUrl) => {
    // Extract the post ID from Instagram URL
    const match =
      instagramUrl.match(/\/p\/([^\/\?]+)/) ||
      instagramUrl.match(/\/reel\/([^\/\?]+)/);
    if (match) {
      // Use correct embed format for Instagram
      return `https://www.instagram.com/p/${match[1]}/embed/`;
    }
    return instagramUrl;
  };

  if (status === 'loading') {
    return (
      <main className="min-h-screen bg-yellow-50">
        <div className="max-w-screen-xl pt-20 md:pt-24 lg:pt-32 pb-24 md:pb-20 lg:pb-32 mx-auto px-3 md:px-4 lg:px-6">
          <Back />
          <div className="text-center py-12">
            <div className="text-gray-600 text-lg">Loading reels...</div>
          </div>
        </div>
      </main>
    );
  }

  if (status === 'failed' || !finalStories || finalStories.length === 0) {
    return (
      <main className="min-h-screen bg-yellow-50">
        <div className="max-w-screen-xl pt-20 md:pt-24 lg:pt-32 pb-24 md:pb-20 lg:pb-32 mx-auto px-3 md:px-4 lg:px-6">
          <Back />
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎬</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Reels Available</h2>
            <p className="text-gray-600">Check back later for new content!</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen banner">
      <div className="max-w-screen-xl pt-20 md:pt-24 lg:pt-32 pb-24 md:pb-20 lg:pb-32 mx-auto px-3 md:px-4 lg:px-6">
        <Back />
        {/* Stories Container */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 max-w-6xl mx-auto">
          {finalStories.map((story) => (
            <div
              key={story.id}
              onClick={() => handleStoryClick(story)}
              className="cursor-pointer group"
            >
              <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                {/* Story Image */}
                <img
                  src={story.thumbnail}
                  alt={story.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to a solid color background with text
                    e.target.style.display = "none";
                    const fallbackDiv = e.target.nextSibling;
                    if (fallbackDiv) {
                      fallbackDiv.style.display = "flex";
                    }
                  }}
                />

                {/* Fallback Background */}
                <div className="hidden w-full h-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center">
                  <div className="text-center text-white">
                    <FaPlay className="text-2xl md:text-3xl mx-auto mb-2" />
                    <div className="bg-black/60 backdrop-blur-sm rounded-md px-2 py-1">
                      <p className="text-xs md:text-sm font-bold px-1">{story.title}</p>
                    </div>
                  </div>
                </div>

                {/* Play Button Overlay */}
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white/90 rounded-full p-3">
                    <FaPlay className="text-gray-800 text-lg" />
                  </div>
                </div>

                {/* Story Title */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  <div className="bg-black/60 backdrop-blur-sm rounded-md px-2 py-1">
                    <p className="text-white font-bold truncate text-sm">{story.title}</p>
                  </div>
                </div>

                {/* Platform Icon */}
                <div className="absolute top-3 right-3">
                  <FaPlay className="text-white text-sm bg-black/60 rounded-full p-1" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Video Modal */}
        {isModalOpen && selectedStory && (
        <div 
          className="fixed inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          style={{ zIndex: 99999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div 
            className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl overflow-hidden shadow-2xl"
            style={{ zIndex: 100000 }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white">
              <div className="flex items-center space-x-2">
                <FaPlay className="text-white text-sm" />
                <h3 className="text-sm font-medium truncate max-w-[200px]">{selectedStory.title}</h3>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => openExternalLink(selectedStory.instagramUrl)}
                  className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                  title="Open in new tab"
                >
                  <FaExternalLinkAlt className="text-sm" />
                </button>
                <button
                  onClick={closeModal}
                  className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                  title="Close"
                >
                  <span className="text-white text-xl font-bold">×</span>
                </button>
              </div>
            </div>

            {/* Instagram Embed */}
            <div className="relative bg-white" style={{
              height: window.innerWidth < 768 ? "60vh" : "70vh", 
              minHeight: window.innerWidth < 768 ? "400px" : "600px", 
              maxHeight: window.innerWidth < 768 ? "500px" : "700px"
            }}> 
              <div className="flex items-center justify-center" style={{height : "100%"}}>
                {useFallbackMode ? (
                  /* Fallback mode - direct iframe only */
                  <iframe
                    src={selectedStory.embedUrl}
                    width="400"
                    height="600"
                    frameBorder="0"
                    scrolling="no"
                    allowtransparency="true"
                    style={{
                      maxWidth: '400px',
                      width: '100%',
                      height: '100%'
                    }}
                    onError={(e) => {
                      console.warn('Error loading iframe in fallback mode:', e);
                    }}
                  />
                ) : (
                  <>
                    {/* Try blockquote embed first */}
                    <blockquote 
                      className="instagram-media" 
                      data-instgrm-permalink={selectedStory.instagramUrl}
                      data-instgrm-version="14" 
                      style={{margin:0, padding:0, width: "100%", height: "100%", maxWidth: "400px"}}
                    >
                    </blockquote>
                    
                    {/* Fallback iframe if blockquote doesn't work */}
                    <iframe
                      src={selectedStory.embedUrl}
                      width="400"
                      height="600"
                      frameBorder="0"
                      scrolling="no"
                      allowtransparency="true"
                      style={{
                        display: 'none',
                        maxWidth: '400px',
                        width: '100%',
                        height: '100%'
                      }}
                      onLoad={(e) => {
                        try {
                          // Show iframe if blockquote fails to load
                          setTimeout(() => {
                            const blockquote = e.target.previousSibling;
                            if (blockquote && blockquote.children.length === 0) {
                              e.target.style.display = 'block';
                              blockquote.style.display = 'none';
                            }
                          }, 2000);
                        } catch (error) {
                          console.warn('Error in iframe onLoad:', error);
                        }
                      }}
                      onError={(e) => {
                        console.warn('Error loading iframe:', e);
                        setUseFallbackMode(true); // Switch to fallback mode
                      }}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FaInstagram className="text-pink-500" />
                  <span className="text-sm text-gray-600">
                    Watch on Instagram
                  </span>
                </div>
                <button
                  onClick={() => openExternalLink(selectedStory.instagramUrl)}
                  className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full text-sm font-medium hover:from-pink-600 hover:to-purple-700 transition-all duration-300"
                >
                  View on Instagram
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </main>
  );
};

export default ReelsScreen;
